import {
  AccessListSchema,
  AccountPrivacySchema,
  PersonalDataExportSchema,
  PrivacyRequestSchema,
  PrivacyResolveResponseSchema,
  type CommunicationsPatch,
  type PrivacyRequestCreate,
  type PrivacyResolveRequest,
  ApplyRunResponseSchema,
  CalendarFeedSchema,
  ChurchDemoResponseSchema,
  ClearChurchDataResponseSchema,
  DistributionSchema,
  EventResourceSchema,
  ImportResponseSchema,
  InviteResponseSchema,
  ProfileLinkSchema,
  MeSchema,
  MemberAccessSchema,
  MemberSchema,
  MinistrySchema,
  NotificationsResponseSchema,
  NudgeResultSchema,
  OrganizationSchema,
  OrgListSchema,
  OrgSummarySchema,
  RegistrationDecisionSchema,
  RegistrationLinkSchema,
  RoleSchema,
  VisitorLinkSchema,
  VisitorReferralSchema,
  VisitorSchema,
  type FollowUpCreate,
  type FollowUpPatch,
  type VisitorContactRequest,
  type VisitorCreate,
  type VisitorInput,
  SlugCheckSchema,
  ScheduleResultSchema,
  ScheduleRunSchema,
  SettingsSchema,
  StateSchema,
  SwapCandidatesSchema,
  type SwapCreateRequest,
  type MemberBulkEditRequest,
  type AssignRequest,
  type AttendanceRequest,
  type AudienceRequest,
  type BackfillRequest,
  type OccurrenceRequest,
  type RestoreSkippedRequest,
  type SkipOccurrenceRequest,
  type HistoryRolesRequest,
  type ServedRequest,
  type EventCreate,
  type EventUpdate,
  type GenerationRequest,
  type MemberCreate,
  type MemberInput,
  type MinistryCreate,
  type MinistryInput,
  type MoveRequest,
  type PhotoUpload,
  type OrgCreate,
  type OrgPatch,
  type PreferencesRequest,
  type RespondRequest,
  type RolePatch,
  type SettingsPatch,
  type SlotsRestore,
  type UndoResponseRequest,
  type WhatsAppRequest,
} from '@escala/contracts';
import type { EventScope } from '@escala/domain/transitions';
import type { ProposalAdjustment } from '@escala/domain/generator';
import type { MergeField, OrgStatus, ScheduleEvent, StoredRole } from '@escala/domain/types';
import { z } from 'zod';

import { authorized } from './session';

/**
 * As rotas da API, uma função por rota, com a resposta validada pelo schema de
 * `@escala/contracts` (o mesmo que o servidor usa para gerar a OpenAPI). Quem chama é a
 * sincronização da store (`store/remote.ts`); as telas não conhecem este módulo.
 */

const API = '/api/v1';
const json = (method: string, body?: unknown) => ({ method, ...(body === undefined ? {} : { body }) });
const ifMatch = (version: number | undefined): Record<string, string> =>
  version === undefined ? {} : { 'If-Match': `"${version}"` };
const id = encodeURIComponent;

export const api = {
  me: () => authorized(`${API}/auth/me`, { schema: MeSchema }),
  savePreferences: (body: PreferencesRequest) =>
    authorized(`${API}/auth/me/preferences`, json('PATCH', body)),
  state: () => authorized(`${API}/state`, { schema: StateSchema }),

  // Ministérios e funções
  createMinistry: (body: MinistryCreate) =>
    authorized(`${API}/ministries`, { ...json('POST', body), schema: MinistrySchema }),
  updateMinistry: (ministryId: string, body: MinistryInput) =>
    authorized(`${API}/ministries/${id(ministryId)}`, { ...json('PATCH', body), schema: MinistrySchema }),
  deleteMinistry: (ministryId: string) => authorized(`${API}/ministries/${id(ministryId)}`, json('DELETE')),
  addRole: (ministryId: string, roleId: string) =>
    authorized(`${API}/ministries/${id(ministryId)}/roles`, {
      ...json('POST', { id: roleId }),
      schema: RoleSchema,
    }),
  updateRole: (roleId: string, body: RolePatch) =>
    authorized(`${API}/roles/${id(roleId)}`, { ...json('PATCH', body), schema: RoleSchema }),
  deleteRole: (roleId: string) => authorized(`${API}/roles/${id(roleId)}`, json('DELETE')),
  enableInRole: (roleId: string, memberId: string) =>
    authorized(`${API}/roles/${id(roleId)}/members/${id(memberId)}`, json('PUT')),
  disableInRole: (roleId: string, memberId: string) =>
    authorized(`${API}/roles/${id(roleId)}/members/${id(memberId)}`, json('DELETE')),

  // Membros
  createMember: (body: MemberCreate) =>
    authorized(`${API}/members`, { ...json('POST', body), schema: MemberSchema }),
  saveMember: (memberId: string, body: MemberInput) =>
    authorized(`${API}/members/${id(memberId)}`, { ...json('PUT', body), schema: MemberSchema }),
  setMemberPhoto: (memberId: string, photo: PhotoUpload | null) =>
    authorized(`${API}/members/${id(memberId)}/photo`, {
      ...(photo ? json('PUT', photo) : json('DELETE')),
      schema: MemberSchema,
    }),
  createProfileLink: (memberId: string) =>
    authorized(`${API}/members/${id(memberId)}/profile-link`, { ...json('POST'), schema: ProfileLinkSchema }),
  bulkEditMembers: (body: MemberBulkEditRequest) =>
    authorized(`${API}/members/bulk`, { ...json('POST', body), schema: z.array(MemberSchema) }),
  importMembers: (csv: string, idSeed: string) =>
    authorized(`${API}/members/import`, { ...json('POST', { csv, idSeed }), schema: ImportResponseSchema }),

  // Eventos
  createEvents: (body: EventCreate) =>
    authorized(`${API}/events`, { ...json('POST', body), schema: z.array(EventResourceSchema) }),
  updateEvents: (eventId: string, body: EventUpdate, version?: number) =>
    authorized(`${API}/events/${id(eventId)}`, {
      ...json('PATCH', body),
      headers: ifMatch(version),
      schema: z.array(EventResourceSchema),
    }),
  deleteEvents: (eventId: string, scope: EventScope) =>
    authorized(`${API}/events/${id(eventId)}`, { method: 'DELETE', query: { scope } }),
  restoreEvent: ({ id: eventId, ...event }: ScheduleEvent) =>
    authorized(`${API}/events/${id(eventId)}`, { ...json('PUT', event), schema: EventResourceSchema }),
  backfill: (seriesId: string, body: BackfillRequest) =>
    authorized(`${API}/events/series/${id(seriesId)}/backfill`, {
      ...json('POST', body),
      schema: z.array(EventResourceSchema),
    }),
  skipOccurrence: (eventId: string, body: SkipOccurrenceRequest) =>
    authorized(`${API}/events/${id(eventId)}/skip`, {
      ...json('POST', body),
      schema: z.array(EventResourceSchema),
    }),
  restoreSkipped: (seriesId: string, body: RestoreSkippedRequest) =>
    authorized(`${API}/events/series/${id(seriesId)}/skipped/restore`, {
      ...json('POST', body),
      schema: z.array(EventResourceSchema),
    }),
  addOccurrence: (seriesId: string, body: OccurrenceRequest) =>
    authorized(`${API}/events/series/${id(seriesId)}/occurrences`, {
      ...json('POST', body),
      schema: EventResourceSchema,
    }),

  // Histórico (quem serviu nos eventos passados)
  served: (eventId: string, body: ServedRequest, version?: number) =>
    authorized(`${API}/events/${id(eventId)}/history/served`, {
      ...json('POST', body),
      headers: ifMatch(version),
      schema: ScheduleResultSchema,
    }),
  copyServed: (eventId: string, body: HistoryRolesRequest, version?: number) =>
    authorized(`${API}/events/${id(eventId)}/history/copy-previous`, {
      ...json('POST', body),
      headers: ifMatch(version),
      schema: ScheduleResultSchema,
    }),
  clearServed: (eventId: string, body: HistoryRolesRequest, version?: number) =>
    authorized(`${API}/events/${id(eventId)}/history/clear`, {
      ...json('POST', body),
      headers: ifMatch(version),
      schema: ScheduleResultSchema,
    }),
  setRecorded: (eventId: string, recorded: boolean, version?: number) =>
    authorized(`${API}/events/${id(eventId)}/history/recorded`, {
      ...json('PUT', { recorded }),
      headers: ifMatch(version),
      schema: ScheduleResultSchema,
    }),

  // Escala
  assign: (eventId: string, body: AssignRequest, version?: number) =>
    authorized(`${API}/events/${id(eventId)}/assignments`, {
      ...json('POST', body),
      headers: ifMatch(version),
      schema: ScheduleResultSchema,
    }),
  remove: (eventId: string, roleId: string, memberId: string, version?: number) =>
    authorized(`${API}/events/${id(eventId)}/assignments/${id(roleId)}/${id(memberId)}`, {
      method: 'DELETE',
      headers: ifMatch(version),
      schema: ScheduleResultSchema,
    }),
  move: (eventId: string, body: MoveRequest, version?: number) =>
    authorized(`${API}/events/${id(eventId)}/assignments/move`, {
      ...json('POST', body),
      headers: ifMatch(version),
      schema: ScheduleResultSchema,
    }),
  restoreSlots: (eventId: string, body: SlotsRestore, message: string, version?: number) =>
    authorized(`${API}/events/${id(eventId)}/slots`, {
      ...json('PUT', body),
      query: { message },
      headers: ifMatch(version),
      schema: ScheduleResultSchema,
    }),
  publish: (eventId: string, version?: number) =>
    authorized(`${API}/events/${id(eventId)}/publish`, {
      method: 'POST',
      headers: ifMatch(version),
      schema: ScheduleResultSchema,
    }),

  // Presença e check-in
  undoResponse: (eventId: string, body: UndoResponseRequest) =>
    authorized(`${API}/events/${id(eventId)}/response/undo`, {
      ...json('POST', body),
      schema: EventResourceSchema,
    }),
  respond: (eventId: string, body: RespondRequest) =>
    authorized(`${API}/events/${id(eventId)}/response`, {
      ...json('POST', body),
      schema: EventResourceSchema,
    }),
  recordAttendance: (eventId: string, body: AttendanceRequest) =>
    authorized(`${API}/events/${id(eventId)}/attendance`, {
      ...json('PUT', body),
      schema: ScheduleResultSchema,
    }),
  recordAudience: (eventId: string, body: AudienceRequest) =>
    authorized(`${API}/events/${id(eventId)}/audience`, {
      ...json('PUT', body),
      schema: EventResourceSchema,
    }),
  swapCandidates: (eventId: string) =>
    authorized(`${API}/events/${id(eventId)}/swaps/candidates`, { schema: SwapCandidatesSchema }),
  requestSwap: (eventId: string, body: SwapCreateRequest) =>
    authorized(`${API}/events/${id(eventId)}/swaps`, { ...json('POST', body), schema: EventResourceSchema }),
  cancelSwap: (eventId: string, swapId: string) =>
    authorized(`${API}/events/${id(eventId)}/swaps/${id(swapId)}/cancel`, {
      method: 'POST',
      schema: EventResourceSchema,
    }),
  answerSwap: (eventId: string, swapId: string, accept: boolean) =>
    authorized(`${API}/events/${id(eventId)}/swaps/${id(swapId)}/answer`, {
      ...json('POST', { accept }),
      schema: EventResourceSchema,
    }),
  decideSwap: (eventId: string, swapId: string, approve: boolean) =>
    authorized(`${API}/events/${id(eventId)}/swaps/${id(swapId)}/decision`, {
      ...json('POST', { approve }),
      schema: EventResourceSchema,
    }),
  nudge: (eventId: string) =>
    authorized(`${API}/events/${id(eventId)}/nudge`, { method: 'POST', schema: NudgeResultSchema }),
  whatsapp: (eventId: string, body: WhatsAppRequest) =>
    authorized(`${API}/events/${id(eventId)}/whatsapp`, json('POST', body)),
  setMemberInactive: (memberId: string, inactive: boolean) =>
    authorized(`${API}/members/${id(memberId)}/${inactive ? 'deactivate' : 'reactivate'}`, {
      method: 'POST',
      schema: MemberSchema,
    }),
  setUnavailableDate: (memberId: string, date: string, unavailable: boolean) =>
    authorized(`${API}/members/${id(memberId)}/unavailable-dates/${id(date)}`, {
      method: unavailable ? 'PUT' : 'DELETE',
      schema: MemberSchema,
    }),

  // Agenda pessoal
  calendarFeed: () => authorized(`${API}/calendar/feed`, { schema: CalendarFeedSchema }),
  rotateCalendarFeed: () =>
    authorized(`${API}/calendar/feed/rotate`, { method: 'POST', schema: CalendarFeedSchema }),

  // Geração
  requestGeneration: (body: GenerationRequest) =>
    authorized(`${API}/schedule-runs`, { ...json('POST', body), schema: ScheduleRunSchema }),
  generationRun: (runId: string) =>
    authorized(`${API}/schedule-runs/${id(runId)}`, { schema: ScheduleRunSchema }),
  applyGeneration: (runId: string, publish: boolean, adjustments: readonly ProposalAdjustment[] = []) =>
    authorized(`${API}/schedule-runs/${id(runId)}/apply`, {
      ...json('POST', adjustments.length ? { publish, adjustments } : { publish }),
      schema: ApplyRunResponseSchema,
    }),

  // Configurações, avisos, distribuição
  updateSettings: (body: SettingsPatch) =>
    authorized(`${API}/settings`, { ...json('PATCH', body), schema: SettingsSchema }),
  setOnboarding: (hidden: boolean) => authorized(`${API}/onboarding`, json('PATCH', { hidden })),
  loadDemoData: () =>
    authorized(`${API}/settings/demo-data`, { ...json('POST'), schema: ChurchDemoResponseSchema }),
  clearChurchData: () =>
    authorized(`${API}/settings/clear-data`, { ...json('POST'), schema: ClearChurchDataResponseSchema }),
  markRead: (ids: string[]) => authorized(`${API}/notifications/read`, json('POST', { ids })),
  notifications: () => authorized(`${API}/notifications`, { schema: NotificationsResponseSchema }),
  distribution: (query: { start?: string; weeks?: number; ministryId?: string }) =>
    authorized(`${API}/distribution`, { query, schema: DistributionSchema }),

  // Acessos
  accesses: async () => (await authorized(`${API}/accesses`, { schema: AccessListSchema })).items,
  access: (memberId: string) => authorized(`${API}/accesses/${id(memberId)}`, { schema: MemberAccessSchema }),
  invite: (memberIds: string[]) =>
    authorized(`${API}/accesses/invitations`, {
      ...json('POST', { memberIds }),
      schema: InviteResponseSchema,
    }),
  changeAccessRole: (memberId: string, role: StoredRole) =>
    authorized(`${API}/accesses/${id(memberId)}`, { ...json('PATCH', { role }), schema: MemberAccessSchema }),
  revokeAccess: (memberId: string) =>
    authorized(`${API}/accesses/${id(memberId)}`, { method: 'DELETE', schema: MemberAccessSchema }),
  endMemberSessions: (memberId: string) =>
    authorized(`${API}/accesses/${id(memberId)}/sessions/end`, json('POST')),

  // Organizações (a plataforma; o Administrador da organização edita a própria)
  orgs: async () => (await authorized(`${API}/orgs`, { schema: OrgListSchema })).items,
  createOrg: (body: OrgCreate) =>
    authorized(`${API}/orgs`, { ...json('POST', body), schema: OrgSummarySchema }),
  updateOrg: (orgId: string, body: OrgPatch) =>
    authorized(`${API}/orgs/${id(orgId)}`, { ...json('PATCH', body), schema: OrganizationSchema }),
  inviteOrgAdmin: (orgId: string, email?: string) =>
    authorized(`${API}/orgs/${id(orgId)}/admin-invite`, {
      ...json('POST', email ? { email } : {}),
      schema: OrgSummarySchema,
    }),
  setOrgStatus: (orgId: string, status: OrgStatus) =>
    authorized(`${API}/orgs/${id(orgId)}/status`, { ...json('POST', { status }), schema: OrgSummarySchema }),
  slugCheck: (slug: string, orgId?: string) =>
    authorized(`${API}/orgs/slug-check`, { query: { slug, orgId }, schema: SlugCheckSchema }),

  // Cadastro público: o link e a aprovação
  rotateRegistrationLink: () =>
    authorized(`${API}/registration-link/rotate`, { method: 'POST', schema: RegistrationLinkSchema }),
  setRegistrationLinkActive: (active: boolean) =>
    authorized(`${API}/registration-link`, { ...json('PATCH', { active }), schema: RegistrationLinkSchema }),
  approveRegistration: (registrationId: string, memberId: string) =>
    authorized(`${API}/registrations/${id(registrationId)}/approve`, {
      ...json('POST', { memberId }),
      schema: RegistrationDecisionSchema,
    }),
  mergeRegistration: (registrationId: string, memberId: string, fields: MergeField[]) =>
    authorized(`${API}/registrations/${id(registrationId)}/merge`, {
      ...json('POST', { memberId, fields }),
      schema: RegistrationDecisionSchema,
    }),
  rejectRegistration: (registrationId: string, reason: string) =>
    authorized(`${API}/registrations/${id(registrationId)}/reject`, {
      ...json('POST', { reason }),
      schema: RegistrationDecisionSchema,
    }),

  // Visitantes e integração
  createVisitor: (body: VisitorCreate) =>
    authorized(`${API}/visitors`, { ...json('POST', body), schema: VisitorSchema }),
  updateVisitor: (visitorId: string, body: VisitorInput) =>
    authorized(`${API}/visitors/${id(visitorId)}`, { ...json('PUT', body), schema: VisitorSchema }),
  anonymizeVisitor: (visitorId: string) =>
    authorized(`${API}/visitors/${id(visitorId)}`, { method: 'DELETE', schema: VisitorSchema }),
  recordVisitorVisit: (visitorId: string, visit: { date: string; eventId: string | null }) =>
    authorized(`${API}/visitors/${id(visitorId)}/visits`, { ...json('POST', visit), schema: VisitorSchema }),
  moveVisitor: (visitorId: string, stage: string) =>
    authorized(`${API}/visitors/${id(visitorId)}/stage`, {
      ...json('POST', { stage }),
      schema: VisitorSchema,
    }),
  assignVisitorOwner: (visitorId: string, ownerId: string | null) =>
    authorized(`${API}/visitors/${id(visitorId)}/owner`, {
      ...json('POST', { ownerId }),
      schema: VisitorSchema,
    }),
  addVisitorFollowUp: (visitorId: string, body: FollowUpCreate) =>
    authorized(`${API}/visitors/${id(visitorId)}/follow-ups`, {
      ...json('POST', body),
      schema: VisitorSchema,
    }),
  updateVisitorFollowUp: (visitorId: string, followUpId: string, body: FollowUpPatch) =>
    authorized(`${API}/visitors/${id(visitorId)}/follow-ups/${id(followUpId)}`, {
      ...json('PATCH', body),
      schema: VisitorSchema,
    }),
  logVisitorContact: (visitorId: string, body: VisitorContactRequest) =>
    authorized(`${API}/visitors/${id(visitorId)}/contacts`, { ...json('POST', body), schema: VisitorSchema }),
  referVisitor: (visitorId: string, registrationId: string) =>
    authorized(`${API}/visitors/${id(visitorId)}/registration`, {
      ...json('POST', { registrationId }),
      schema: VisitorReferralSchema,
    }),
  linkVisitorRegistration: (visitorId: string, registrationId: string) =>
    authorized(`${API}/visitors/${id(visitorId)}/registration-link`, {
      ...json('POST', { registrationId }),
      schema: VisitorSchema,
    }),
  rotateVisitorLink: () =>
    authorized(`${API}/visitor-link/rotate`, { method: 'POST', schema: VisitorLinkSchema }),
  setVisitorLinkActive: (active: boolean) =>
    authorized(`${API}/visitor-link`, { ...json('PATCH', { active }), schema: VisitorLinkSchema }),

  // Privacidade e dados (LGPD)
  privacyAccount: () => authorized(`${API}/privacy/account`, { schema: AccountPrivacySchema }),
  acceptTerms: (version: string) =>
    authorized(`${API}/privacy/terms`, { ...json('POST', { version }), schema: AccountPrivacySchema }),
  setCommunications: (body: CommunicationsPatch) =>
    authorized(`${API}/privacy/communications`, { ...json('PATCH', body), schema: AccountPrivacySchema }),
  exportData: () => authorized(`${API}/privacy/export`, { schema: PersonalDataExportSchema }),
  submitPrivacyRequest: (body: PrivacyRequestCreate) =>
    authorized(`${API}/privacy/requests`, { ...json('POST', body), schema: PrivacyRequestSchema }),
  resolvePrivacyRequest: (requestId: string, body: PrivacyResolveRequest) =>
    authorized(`${API}/privacy/requests/${id(requestId)}/resolve`, {
      ...json('POST', body),
      schema: PrivacyResolveResponseSchema,
    }),
};

export type Api = typeof api;
